import TextButton from 'LuxKit/Button/TextButton'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import type { FlightDropdownOption } from 'components/Flights/types'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import type { FlightsClassTypes } from 'constants/flight'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { trackFareCabinSelect } from 'lib/flights/flightSearchSubmission'
import { rem } from 'polished'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import FlightsSearchTravellerPicker from '../FlightsSearchResultsHeader/FlightsSearchBar/FlightsSearchTravellerPicker'

const Controls = styled(Group)`
  gap: ${rem(8)};

  > button > svg {
    height: ${rem(16)};
    width: ${rem(16)};
  }
`

const MobileResultsTravellerPicker = styled(FlightsSearchTravellerPicker)`
  width: min(${rem(385)}, calc(100vw - ${rem(32)}));

  ${mediaQueryUp.tablet} {
    width: ${rem(400)};
  }
`

type ActiveDropdown = 'fare-type' | 'travellers' | 'fare-cabin'

interface DropdownOptionProps {
  option: FlightDropdownOption
  selected: boolean
  onSelect: (option: FlightDropdownOption) => void
}

function MobileResultsDropdownOption(props: DropdownOptionProps) {
  const { option, selected, onSelect } = props

  const handleSelect = useCallback(() => {
    onSelect(option)
  }, [onSelect, option])

  return <ClickableListItem
    title={option.label}
    endIcon={selected ? <LineCheckIcon size="XS" /> : undefined}
    selected={selected}
    disabled={option.disabled}
    onClick={handleSelect}
  />
}

interface Props {
  fareOptions: Array<FlightDropdownOption>
  fareCabinOptions: Array<FlightDropdownOption>
  onFareTypeChange: (fareType: FlightDropdownOption) => void
}

function FlightSearchWidgetMobileResultsControls(props: Props) {
  const { fareOptions, fareCabinOptions, onFareTypeChange } = props
  const intl = useIntl()
  const { fareType, fareCabin, occupants } = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const [activeDropdown, setActiveDropdown] = useState<ActiveDropdown>()
  const controlsRef = useRef<HTMLDivElement>(null)
  const fareTypeTriggerRef = useRef<HTMLButtonElement>(null)
  const travellersTriggerRef = useRef<HTMLButtonElement>(null)
  const fareCabinTriggerRef = useRef<HTMLButtonElement>(null)
  const initialTravellersRef = useRef(occupants)

  const travellerCount = useMemo(
    () => occupants.adults + (occupants.children || 0) + (occupants.infants || 0),
    [occupants],
  )

  const closeDropdown = useCallback(() => {
    setActiveDropdown(undefined)
  }, [])

  const toggleFareTypeDropdown = useCallback(() => {
    setActiveDropdown((active) => active === 'fare-type' ? undefined : 'fare-type')
  }, [])

  const toggleTravellersDropdown = useCallback(() => {
    setActiveDropdown((active) => {
      if (active === 'travellers') {
        return undefined
      }

      initialTravellersRef.current = occupants
      return 'travellers'
    })
  }, [occupants])

  const toggleFareCabinDropdown = useCallback(() => {
    setActiveDropdown((active) => active === 'fare-cabin' ? undefined : 'fare-cabin')
  }, [])

  const selectFareType = useCallback((option: FlightDropdownOption) => {
    if (option.value === fareType.value) {
      closeDropdown()
      return
    }

    onFareTypeChange(option)
    closeDropdown()
  }, [closeDropdown, fareType.value, onFareTypeChange])

  const selectFareCabin = useCallback((option: FlightDropdownOption) => {
    trackFareCabinSelect(option.value as FlightsClassTypes)
    dispatch({ type: FlightSearchWidgetActions.SET_FARE_CABIN, fareCabin: option })
    closeDropdown()
  }, [closeDropdown, dispatch])

  const changeOccupants = useCallback((occupants: App.Occupants) => {
    dispatch({ type: FlightSearchWidgetActions.SET_OCCUPANTS, occupants })
  }, [dispatch])

  const cancelTravellersSelection = useCallback(() => {
    dispatch({ type: FlightSearchWidgetActions.SET_OCCUPANTS, occupants: initialTravellersRef.current })
    closeDropdown()
  }, [closeDropdown, dispatch])

  const applyLabel = useMemo(() => intl.formatMessage({
    id: 'XV_uWUPjmcqL5r9jwE-x',
    defaultMessage: 'Apply',
    description: 'Button text to apply traveller selection in flight search',
  }), [intl])

  const cancelLabel = useMemo(() => intl.formatMessage({
    id: 'F-YTO1e90A3DqzfDiCrU',
    defaultMessage: 'Cancel',
    description: 'Button text to cancel flight date selection in the flights flow',
  }), [intl])

  const travellerCountLabel = useMemo(() => intl.formatMessage({
    id: 'PJYm3GEnfNrvO_bV4d6I',
    defaultMessage: '{travellerCount, plural, one {# traveller} other {# travellers}}',
    description: 'Accessible label for the traveller count button in the mobile flight search controls',
  }, { travellerCount }), [intl, travellerCount])

  return <Controls ref={controlsRef} direction="horizontal" gap={0} verticalAlign="center">
    <TextButton
      kind="tertiary"
      shape="round"
      isSelected={activeDropdown === 'fare-type'}
      endIcon={<DropdownChevron open={activeDropdown === 'fare-type'} />}
      onClick={toggleFareTypeDropdown}
      ref={fareTypeTriggerRef}
      aria-expanded={activeDropdown === 'fare-type'}
    >
      {fareType.label}
    </TextButton>

    <DropdownList
      size="S"
      anchorRef={fareTypeTriggerRef}
      triggerRef={fareTypeTriggerRef}
      open={activeDropdown === 'fare-type'}
      onClose={closeDropdown}
    >
      {fareOptions.map((option) => <MobileResultsDropdownOption
        key={option.value}
        option={option}
        selected={option.value === fareType.value}
        onSelect={selectFareType}
      />)}
    </DropdownList>

    <TextButton
      kind="tertiary"
      shape="round"
      isSelected={activeDropdown === 'travellers'}
      startIcon={<LineUserIcon size="XS" />}
      endIcon={<DropdownChevron open={activeDropdown === 'travellers'} />}
      onClick={toggleTravellersDropdown}
      ref={travellersTriggerRef}
      aria-expanded={activeDropdown === 'travellers'}
      aria-label={travellerCountLabel}
    >
      {travellerCount}
    </TextButton>

    <DropdownSheet
      size="content"
      anchorRef={controlsRef}
      triggerRef={travellersTriggerRef}
      open={activeDropdown === 'travellers'}
      onClose={closeDropdown}
      placement="bottom-start"
      autoHeight
      noPaddingOnBody
      secondaryActionProps={{
        children: cancelLabel,
        kind: 'tertiary',
        onClick: cancelTravellersSelection,
      }}
      primaryActionProps={{
        children: applyLabel,
        onClick: closeDropdown,
      }}
    >
      <MobileResultsTravellerPicker
        occupants={occupants}
        onChange={changeOccupants}
        showTitle={false}
      />
    </DropdownSheet>

    <TextButton
      kind="tertiary"
      shape="round"
      isSelected={activeDropdown === 'fare-cabin'}
      endIcon={<DropdownChevron open={activeDropdown === 'fare-cabin'} />}
      onClick={toggleFareCabinDropdown}
      ref={fareCabinTriggerRef}
      aria-expanded={activeDropdown === 'fare-cabin'}
    >
      {fareCabin.label}
    </TextButton>

    <DropdownList
      size="S"
      anchorRef={fareCabinTriggerRef}
      triggerRef={fareCabinTriggerRef}
      open={activeDropdown === 'fare-cabin'}
      onClose={closeDropdown}
    >
      {fareCabinOptions.map((option) => <MobileResultsDropdownOption
        key={option.value}
        option={option}
        selected={option.value === fareCabin.value}
        onSelect={selectFareCabin}
      />)}
    </DropdownList>
  </Controls>
}

export default FlightSearchWidgetMobileResultsControls
